<template>
  <div class="revolving-credit-page">
    <v-container fluid class="px-15 pb-10 pt-6">
      <v-row class="ma-0 mt-5 mb-8" align="start">
        <v-col cols="12" md="7" class="pa-0">
          <div class="d-flex align-center flex-wrap">
            <div class="page-title">{{ creditLine.client }}</div>
            <v-chip
              color="#DFF5EE"
              text-color="#009762"
              class="ml-4 status-chip"
            >
              {{ creditLine.status }}
            </v-chip>
          </div>
          <div class="page-subtitle mt-3">
            {{ creditLine.id }} · {{ creditLine.currency }} · Crédito revolvente
          </div>
        </v-col>

        <v-col cols="12" md="5" class="pa-0 d-flex justify-end">
          <v-btn
            color="primary"
            rounded
            class="white--text detail-action-btn"
            @click="dialogPayment = true"
          >
            <span class="mr-3 money-icon">$</span>
            Realizar Pago
          </v-btn>
        </v-col>
      </v-row>

      <v-row class="ma-0 mb-2">
        <v-col
          v-for="(metric, index) in metrics"
          :key="metric.label"
          cols="12"
          sm="6"
          lg="3"
          :class="[
            'pl-0 pb-4',
            index === metrics.length - 1 ? 'pr-0' : 'pr-4'
          ]"
        >
          <v-card outlined class="metric-card">
            <div>
              <div class="metric-label">{{ metric.label }}</div>
              <div class="metric-value">{{ metric.value }}</div>
              <div v-if="metric.helper" class="metric-helper">
                {{ metric.helper }}
              </div>
            </div>

            <div class="metric-icon">
              <v-icon :color="metric.color" size="34">
                {{ metric.icon }}
              </v-icon>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-card outlined class="detail-tabs-card">
        <v-tabs
          v-model="activeTab"
          background-color="white"
          color="primary"
          slider-color="primary"
          class="detail-tabs"
        >
          <v-tab
            v-for="tab in tabs"
            :key="tab.value"
            class="detail-tab"
          >
            {{ tab.label }}
          </v-tab>
        </v-tabs>

        <v-divider />

        <v-tabs-items v-model="activeTab" class="tabs-content">
          <v-tab-item>
            <v-row>
              <v-col cols="12" md="6">
                <div class="section-title">Información del Cliente</div>
                <div
                  v-for="item in clientInfo"
                  :key="item.label"
                  class="info-row"
                >
                  <span>{{ item.label }}</span>
                  <strong>{{ item.value }}</strong>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="section-title">Condiciones del Crédito</div>
                <div
                  v-for="item in financialInfo"
                  :key="item.label"
                  class="info-row"
                >
                  <span>{{ item.label }}</span>
                  <strong>{{ item.value }}</strong>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="info-box">
                  <div class="info-box__title">Cómo funciona</div>
                  <div class="info-box__text">
                    Tu línea se utiliza contra facturas aprobadas. Cuando una
                    disposición se paga, el capital vuelve a estar disponible.
                  </div>
                </div>
              </v-col>

              <v-col cols="12" md="6">
                <div class="info-box">
                  <div class="info-box__title">Aplicación de pagos</div>
                  <div class="info-box__text">
                    Los pagos cubren primero intereses calculados por días
                    transcurridos y después capital.
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-tab-item>

          <v-tab-item>
            <div class="tab-header-row">
              <div>
                <div class="content-title">Financiamientos otorgados</div>
                <div class="content-helper">
                  Cada disposición puede agrupar una o varias facturas financiadas.
                </div>
              </div>
            </div>

            <v-data-table
              :headers="drawHeaders"
              :items="draws"
              hide-default-footer
              disable-sort
              class="detail-table"
            >
              <template v-slot:item.status="{ item }">
                <v-chip
                  :color="item.statusColor"
                  :text-color="item.statusText"
                  class="table-chip"
                >
                  {{ item.status }}
                </v-chip>
              </template>
            </v-data-table>
          </v-tab-item>

          <v-tab-item>
            <div class="tab-header-row">
              <div class="content-title">Historial de pagos</div>
              <v-btn
                outlined
                rounded
                color="primary"
                class="detail-action-btn"
                @click="dialogPayment = true"
              >
                <v-icon left>mdi-bank-transfer</v-icon>
                Ver datos de pago
              </v-btn>
            </div>

            <v-data-table
              :headers="paymentHeaders"
              :items="payments"
              hide-default-footer
              disable-sort
              class="detail-table"
            >
              <template v-slot:item.receipt>
                <v-btn icon color="primary">
                  <v-icon>mdi-eye-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-tab-item>

          <v-tab-item>
            <div class="tab-header-row">
              <div>
                <div class="content-title">Facturas registradas</div>
                <div class="content-helper">
                  Facturas que soportan tus solicitudes de financiamiento.
                </div>
              </div>
              <div class="tab-actions">
                <v-btn
                  outlined
                  rounded
                  color="primary"
                  class="detail-action-btn"
                  @click="dialogInvoice = true"
                >
                  <v-icon left>mdi-file-plus-outline</v-icon>
                  Agregar factura
                </v-btn>
                <v-btn
                  color="primary"
                  rounded
                  class="white--text detail-action-btn"
                  disabled
                >
                  <v-icon left>mdi-bank-transfer-out</v-icon>
                  Solicitar disposición
                </v-btn>
              </div>
            </div>

            <v-data-table
              :headers="invoiceHeaders"
              :items="invoices"
              hide-default-footer
              disable-sort
              class="detail-table"
            >
              <template v-slot:item.status="{ item }">
                <v-chip
                  :color="item.statusColor"
                  :text-color="item.statusText"
                  class="table-chip"
                >
                  {{ item.status }}
                </v-chip>
              </template>

              <template v-slot:item.actions>
                <v-btn icon color="primary">
                  <v-icon>mdi-eye-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-tab-item>

          <v-tab-item>
            <v-data-table
              :headers="documentHeaders"
              :items="documents"
              hide-default-footer
              disable-sort
              class="detail-table"
            >
              <template v-slot:item.actions>
                <v-btn icon color="primary">
                  <v-icon>mdi-download-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <v-dialog v-model="dialogPayment" max-width="860" persistent>
        <v-card class="payment-dialog">
          <v-card-title class="payment-dialog__title">
            <div>
              <div class="payment-title">Realizar Pago</div>
              <div class="payment-subtitle">
                {{ creditLine.id }} · {{ creditLine.client }}
              </div>
            </div>

            <v-btn icon color="#60708a" @click="dialogPayment = false">
              <v-icon size="30">mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="payment-dialog__content">
            <div class="current-state">
              <div class="state-title">Monto sugerido</div>
              <v-row>
                <v-col cols="12" md="6">
                  <div class="state-label">Interés estimado al día:</div>
                  <div class="state-value pending">{{ paymentState.interest }}</div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="state-label">Capital pendiente:</div>
                  <div class="state-value">{{ paymentState.capital }}</div>
                </v-col>
              </v-row>
            </div>

            <div class="bank-card">
              <div class="bank-card__header">
                <div>
                  <div class="bank-title">Información bancaria Fresher</div>
                  <div class="bank-helper">Realiza tu transferencia usando estos datos.</div>
                </div>
                <v-icon color="primary" size="32">mdi-bank-outline</v-icon>
              </div>

              <div
                v-for="item in bankInfo"
                :key="item.label"
                class="bank-row"
              >
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
                <v-btn icon small color="primary" @click="copyToClipboard(item.value)">
                  <v-icon size="18">mdi-content-copy</v-icon>
                </v-btn>
              </div>
            </div>

            <v-row>
              <v-col cols="12" md="6">
                <label class="field-label">Monto que pagarás</label>
                <v-text-field
                  v-model="paymentNotice.amount"
                  outlined
                  hide-details
                  class="dialog-input"
                />
              </v-col>

              <v-col cols="12" md="6">
                <label class="field-label">Referencia bancaria</label>
                <v-text-field
                  v-model="paymentNotice.reference"
                  outlined
                  hide-details
                  class="dialog-input"
                />
              </v-col>

              <v-col cols="12">
                <label class="field-label">Comprobante de pago</label>
                <button type="button" class="receipt-upload" @click="$refs.receiptFile.click()">
                  <v-icon color="primary" size="26">mdi-cloud-upload-outline</v-icon>
                  <span>{{ receiptFileName || "Subir comprobante PDF, PNG o JPG" }}</span>
                </button>
                <input
                  ref="receiptFile"
                  class="hidden-input"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  @change="handleReceiptChange"
                />
              </v-col>

              <v-col cols="12">
                <label class="field-label">Comentarios</label>
                <v-textarea
                  v-model="paymentNotice.comments"
                  placeholder="Notas adicionales sobre tu pago..."
                  outlined
                  hide-details
                  rows="3"
                  no-resize
                  class="dialog-input"
                />
              </v-col>
            </v-row>

            <div class="payment-note">
              <v-icon color="primary" size="22">mdi-information-outline</v-icon>
              <span>
                Este aviso no aplica el pago automáticamente. Fresher validará
                la transferencia, aplicará primero intereses y después capital.
              </span>
            </div>
          </v-card-text>

          <v-divider class="mx-8" />

          <v-card-actions class="payment-actions">
            <v-spacer />
            <v-btn outlined rounded color="primary" class="payment-btn" @click="dialogPayment = false">
              Cancelar
            </v-btn>
            <v-btn color="primary" rounded class="white--text payment-btn" @click="confirmPaymentNotice">
              Enviar aviso de pago
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogInvoice" max-width="860" persistent>
        <v-card class="invoice-dialog">
          <v-card-title class="invoice-dialog__title py-4">
            <div class="invoice-title">Agregar Factura</div>
            <v-btn icon color="#60708a" @click="dialogInvoice = false" style="position: absolute; top: 16px; right: 16px;">
              <v-icon size="30">mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider class="mx-8 my-4" />

          <v-card-text class="invoice-dialog__content">
            <v-row>
              <v-col cols="12" md="6">
                <label class="field-label">Id</label>
                <v-text-field
                  v-model="invoiceForm.id"
                  outlined
                  hide-details
                  class="dialog-input"
                />
              </v-col>

              <v-col cols="12" md="6">
                <label class="field-label">Referencia interna (opcional)</label>
                <v-text-field
                  v-model="invoiceForm.reference"
                  outlined
                  hide-details
                  class="dialog-input"
                />
              </v-col>

              <v-col cols="12" md="6">
                <label class="field-label">Monto</label>
                <v-text-field
                  v-model="invoiceForm.amount"
                  type="number"
                  min="0"
                  outlined
                  hide-details
                  class="dialog-input"
                />
              </v-col>

              <v-col cols="12" md="6">
                <label class="field-label">Moneda</label>
                <v-select
                  v-model="invoiceForm.currency"
                  :items="currencyOptions"
                  outlined
                  hide-details
                  class="dialog-input"
                />
              </v-col>

              <v-col cols="12" md="6">
                <label class="field-label">Fecha de Emisión</label>
                <v-text-field
                  v-model="invoiceForm.issueDate"
                  type="date"
                  outlined
                  hide-details
                  class="dialog-input"
                />
              </v-col>

              <v-col cols="12" md="6">
                <label class="field-label">Fecha de Vencimiento</label>
                <v-text-field
                  v-model="invoiceForm.dueDate"
                  type="date"
                  outlined
                  hide-details
                  class="dialog-input"
                />
              </v-col>

              <v-col cols="12">
                <div class="invoice-total-card">
                  <span>Monto total de la factura</span>
                  <strong>{{ invoiceTotal }}</strong>
                </div>
              </v-col>

              <v-col cols="12">
                <label class="field-label">Documento (PDF)</label>
                <button type="button" class="receipt-upload" @click="$refs.invoiceFile.click()">
                  <v-icon color="primary" size="26">mdi-paperclip</v-icon>
                  <span>{{ invoiceFileName || "Seleccionar documento PDF" }}</span>
                </button>
                <input
                  ref="invoiceFile"
                  class="hidden-input"
                  type="file"
                  accept=".pdf"
                  multiple
                  @change="handleInvoiceFileChange"
                />
                <div v-if="invoiceFiles.length" class="selected-documents">
                  <div
                    v-for="file in invoiceFiles"
                    :key="file.name"
                    class="selected-document"
                  >
                    <v-icon color="primary" size="20">mdi-file-pdf-box</v-icon>
                    <span>{{ file.name }}</span>
                    <small>{{ formatFileSize(file.size) }}</small>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider class="mx-8" />

          <v-card-actions class="invoice-actions py-4" style="justify-content: flex-end;">
            <v-btn
              color="primary"
              rounded
              class="white--text payment-btn"
              @click="saveInvoice"
            >
              Guardar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "ClientRevolvingCredit",
  data() {
    return {
      activeTab: 0,
      dialogPayment: false,
      dialogInvoice: false,
      receiptFileName: "",
      invoiceFileName: "",
      invoiceFiles: [],
      creditLine: {
        client: "Importador Mexicano",
        id: "RC-2026-0001",
        currency: "USD",
        status: "Activa"
      },
      tabs: [
        { label: "Información General", value: "general" },
        { label: "Financiamientos", value: "draws" },
        { label: "Pagos", value: "payments" },
        { label: "Facturas", value: "invoices" },
        { label: "Documentos", value: "documents" }
      ],
      metrics: [
        { label: "Límite Aprobado", value: "$50,000 USD", icon: "mdi-trending-up", color: "#0075B8" },
        { label: "Disponible", value: "$36,600 USD", helper: "Se libera al pagar capital", icon: "mdi-wallet-outline", color: "#009762" },
        { label: "Saldo Pendiente", value: "$13,400 USD", helper: "Capital financiado activo", icon: "mdi-currency-usd", color: "#E65100" },
        { label: "Interés al Día", value: "$335 USD", helper: "Calculado por días transcurridos", icon: "mdi-calendar-clock", color: "#0288D1" }
      ],
      clientInfo: [
        { label: "Cliente:", value: "Importador Mexicano" },
        { label: "RFC:", value: "IME010101AAA" },
        { label: "Email:", value: "finanzas@importadormx.com" }
      ],
      financialInfo: [
        { label: "Límite aprobado:", value: "$50,000 USD" },
        { label: "Disponible:", value: "$36,600 USD" },
        { label: "Tasa mensual:", value: "2.50%" },
        { label: "Moneda:", value: "USD" }
      ],
      drawHeaders: [
        { text: "ID", value: "id" },
        { text: "Facturas", value: "invoiceCount" },
        { text: "Monto dispersado", value: "disbursed" },
        { text: "Fecha dispersado", value: "date" },
        { text: "Interés al día", value: "interest" },
        { text: "Pagado", value: "paid" },
        { text: "Pendiente", value: "pending" },
        { text: "Status", value: "status" }
      ],
      draws: [
        {
          id: "DISP-001",
          invoiceCount: "3 facturas",
          disbursed: "$10,000 USD",
          date: "17 mar 2026",
          interest: "$250 USD",
          paid: "$4,000 USD",
          pending: "$6,250 USD",
          status: "Activa",
          statusColor: "#FFE7E7",
          statusText: "#E53935"
        },
        {
          id: "DISP-002",
          invoiceCount: "1 factura",
          disbursed: "$7,400 USD",
          date: "25 feb 2026",
          interest: "$185 USD",
          paid: "$0 USD",
          pending: "$7,585 USD",
          status: "Activa",
          statusColor: "#FFE7E7",
          statusText: "#E53935"
        },
        {
          id: "DISP-003",
          invoiceCount: "2 facturas",
          disbursed: "$5,000 USD",
          date: "03 feb 2026",
          interest: "$0 USD",
          paid: "$5,110 USD",
          pending: "$0 USD",
          status: "Pagada",
          statusColor: "#DFF5EE",
          statusText: "#009762"
        }
      ],
      paymentHeaders: [
        { text: "Fecha", value: "date" },
        { text: "Monto total", value: "amount" },
        { text: "A interés", value: "interest" },
        { text: "A capital", value: "capital" },
        { text: "Disposición", value: "draw" },
        { text: "Referencia", value: "reference" },
        { text: "Comprobante", value: "receipt", align: "center" }
      ],
      payments: [
        {
          date: "23 mar 2026",
          amount: "$4,010 USD",
          interest: "$10 USD",
          capital: "$4,000 USD",
          draw: "DISP-001",
          reference: "PAY-20260323"
        },
        {
          date: "04 mar 2026",
          amount: "$5,110 USD",
          interest: "$110 USD",
          capital: "$5,000 USD",
          draw: "DISP-003",
          reference: "PAY-20260304"
        }
      ],
      invoiceHeaders: [
        { text: "Proveedor", value: "supplier" },
        { text: "Factura", value: "invoice" },
        { text: "Referencia", value: "reference" },
        { text: "Monto", value: "amount" },
        { text: "Fecha emisión", value: "issuedAt" },
        { text: "Vencimiento", value: "dueDate" },
        { text: "Status", value: "status" },
        { text: "Acciones", value: "actions", align: "center" }
      ],
      invoices: [
        {
          supplier: "Proveedor Agrícola Norte",
          invoice: "FAC-12345",
          reference: "PO-8091",
          amount: "$10,000 USD",
          issuedAt: "05 mar 2026",
          dueDate: "04 abr 2026",
          status: "Financiada",
          statusColor: "#DDF0FF",
          statusText: "#1E88E5"
        },
        {
          supplier: "Empaque del Bajío",
          invoice: "FAC-88812",
          reference: "PO-8102",
          amount: "$7,400 USD",
          issuedAt: "25 feb 2026",
          dueDate: "25 mar 2026",
          status: "Financiada",
          statusColor: "#DDF0FF",
          statusText: "#1E88E5"
        },
        {
          supplier: "Logística Sur",
          invoice: "FAC-77120",
          reference: "PO-7900",
          amount: "$5,000 USD",
          issuedAt: "03 feb 2026",
          dueDate: "03 mar 2026",
          status: "Pagada",
          statusColor: "#DFF5EE",
          statusText: "#009762"
        }
      ],
      documentHeaders: [
        { text: "Documento", value: "name" },
        { text: "Relacionado con", value: "relatedTo" },
        { text: "Fecha", value: "date" },
        { text: "Acciones", value: "actions", align: "center" }
      ],
      documents: [
        { name: "Contrato de crédito revolvente", relatedTo: "Línea RC-2026-0001", date: "01 feb 2026" },
        { name: "Comprobante dispersión DISP-001", relatedTo: "DISP-001", date: "17 mar 2026" },
        { name: "Comprobante pago PAY-20260323", relatedTo: "DISP-001", date: "23 mar 2026" }
      ],
      clientOptions: [
        "Importador Mexicano",
        "Tomate Produce LLC",
        "Aguacates Produce"
      ],
      currencyOptions: ["USD", "MXN"],
      invoiceForm: {
        client: "Importador Mexicano",
        id: "",
        reference: "",
        amount: "",
        currency: "USD",
        issueDate: "",
        dueDate: ""
      },
      paymentState: {
        interest: "$335 USD",
        capital: "$13,400 USD"
      },
      bankInfo: [
        { label: "Banco", value: "BBVA México" },
        { label: "Beneficiario", value: "Fresher Pay S.A. de C.V." },
        { label: "CLABE", value: "012180001234567890" },
        { label: "Cuenta", value: "0012345678" },
        { label: "Referencia", value: "FP-RC-2026-0001" },
        { label: "Concepto", value: "Pago crédito revolvente Importador Mexicano" }
      ],
      paymentNotice: {
        amount: "$13,735 USD",
        reference: "FP-RC-2026-0001",
        comments: ""
      }
    };
  },
  computed: {
    invoiceTotal() {
      const amount = Number(this.invoiceForm.amount || 0);
      const formattedAmount = amount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return `$${formattedAmount} ${this.invoiceForm.currency}`;
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    copyToClipboard(value) {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(value);
      }
    },
    handleReceiptChange(event) {
      const file = event.target.files[0];
      this.receiptFileName = file ? file.name : "";
    },
    handleInvoiceFileChange(event) {
      const files = Array.from(event.target.files || []);
      this.invoiceFiles = files.map((file) => ({
        name: file.name,
        size: file.size
      }));

      if (!files.length) {
        this.invoiceFileName = "";
        return;
      }

      this.invoiceFileName = files.length === 1
        ? files[0].name
        : `${files.length} documentos seleccionados`;
    },
    formatFileSize(size) {
      if (!size) {
        return "";
      }

      if (size < 1024 * 1024) {
        return `${Math.round(size / 1024)} KB`;
      }

      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    },
    saveInvoice() {
      this.dialogInvoice = false;
      if (this.mixSuccess) {
        this.mixSuccess("Factura guardada para revisión.");
      }
    },
    confirmPaymentNotice() {
      this.dialogPayment = false;
      if (this.mixSuccess) {
        this.mixSuccess("Aviso de pago enviado para validación.");
      }
    }
  }
};
</script>

<style scoped>
.revolving-credit-page {
  min-height: 100vh;
  background: #f7f8fb;
}

.page-title {
  color: #1f2330;
  font-family: 'montserrat-bold';
  font-size: 34px;
  line-height: 1.15;
}

.page-subtitle,
.metric-label,
.metric-helper,
.section-title,
.info-row span,
.info-box__text,
.summary-row span,
.content-helper {
  color: #60708a;
}

.page-subtitle {
  font-family: 'montserrat-medium';
  font-size: 18px;
}

.status-chip,
.table-chip {
  font-family: 'montserrat-bold';
}

.detail-action-btn {
  min-height: 46px !important;
  padding: 0 22px !important;
  font-family: 'montserrat-bold';
  font-size: 16px;
  text-transform: none;
}

.money-icon {
  font-family: 'montserrat-bold';
  font-size: 20px;
}

.metric-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  min-height: 170px;
  padding: 36px 38px;
  border-radius: 12px;
  height: 100%;
}

.metric-label {
  font-family: 'montserrat-bold';
  font-size: 18px;
}

.metric-value {
  margin-top: 18px;
  color: #1f2330;
  font-family: 'montserrat-bold';
  font-size: 32px;
  line-height: 1;
}

.metric-helper {
  margin-top: 14px;
  font-family: 'montserrat-medium';
  font-size: 15px;
}

.metric-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 10px;
  background: #f0f3f7;
}

.detail-tabs-card {
  overflow: hidden;
  border-radius: 12px;
}

.detail-tabs ::v-deep .v-slide-group__content {
  padding-left: 24px;
}

.detail-tab {
  min-width: auto;
  padding: 0 26px;
  justify-content: flex-start;
  color: #60708a !important;
  font-family: 'montserrat-bold';
  font-size: 17px;
  letter-spacing: 0;
  text-transform: none;
}

.detail-tab.v-tab--active {
  color: var(--v-primary-base) !important;
}

.tabs-content {
  padding: 34px 40px 40px;
}

.section-title {
  margin-bottom: 24px;
  font-family: 'montserrat-bold';
  font-size: 18px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 16px 0;
  border-bottom: 1px solid #dce3ec;
  font-family: 'montserrat-medium';
  font-size: 17px;
}

.info-row strong {
  color: #1f2330;
  font-family: 'montserrat-bold';
  text-align: right;
}

.info-box {
  min-height: 130px;
  padding: 24px;
  border: 1px solid #dce3ec;
  border-radius: 10px;
  background: #ffffff;
}

.info-box__title,
.content-title {
  color: #1f2330;
  font-family: 'montserrat-bold';
}

.info-box__title {
  margin-bottom: 18px;
  font-size: 18px;
}

.info-box__text {
  font-family: 'montserrat-medium';
  font-size: 17px;
  line-height: 1.45;
}

.tab-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.tab-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 12px;
}

.content-title {
  font-size: 22px;
}

.content-helper {
  margin-top: 6px;
  font-family: 'montserrat-medium';
  font-size: 15px;
}

.detail-table {
  font-family: 'montserrat-medium';
}

.detail-table ::v-deep th {
  color: #60708a !important;
  font-family: 'montserrat-bold';
  font-size: 14px !important;
}

.detail-table ::v-deep td {
  color: #1f2330;
  font-size: 14px !important;
}

.payment-dialog,
.invoice-dialog {
  border-radius: 18px;
}

.payment-dialog__title,
.invoice-dialog__title {
  display: flex;
  justify-content: space-between;
  padding: 28px 32px;
}

.payment-title,
.invoice-title {
  color: #1f2330;
  font-family: 'montserrat-bold';
  font-size: 26px;
}

.payment-subtitle,
.bank-helper {
  margin-top: 6px;
  color: #60708a;
  font-family: 'montserrat-medium';
  font-size: 15px;
}

.payment-dialog__content,
.invoice-dialog__content {
  padding: 24px 32px;
}

.invoice-title {
  width: 100%;
  text-align: center;
}

.invoice-total-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 96px;
  border-radius: 14px;
  background: #f4f5f7;
  color: #333333;
}

.invoice-total-card span {
  font-family: 'montserrat-bold';
  font-size: 18px;
}

.invoice-total-card strong {
  margin-top: 10px;
  font-family: 'montserrat-bold';
  font-size: 28px;
}

.current-state,
.bank-card,
.payment-note {
  border-radius: 12px;
  background: #f8fafc;
}

.current-state {
  margin-bottom: 20px;
  padding: 22px;
}

.state-title,
.bank-title {
  color: #1f2330;
  font-family: 'montserrat-bold';
  font-size: 18px;
}

.state-label {
  margin-top: 18px;
  color: #60708a;
  font-family: 'montserrat-medium';
}

.state-value {
  margin-top: 6px;
  color: #1f2330;
  font-family: 'montserrat-bold';
  font-size: 24px;
}

.state-value.pending {
  color: #E65100;
}

.bank-card {
  margin-bottom: 22px;
  padding: 22px;
  border: 1px solid #dbeafe;
  background: #f8fbff;
}

.bank-card__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.bank-row {
  display: grid;
  grid-template-columns: 150px 1fr 34px;
  align-items: center;
  gap: 12px;
  padding: 11px 0;
  border-bottom: 1px solid #dce3ec;
  font-family: 'montserrat-medium';
}

.bank-row:last-child {
  border-bottom: 0;
}

.bank-row span {
  color: #60708a;
}

.bank-row strong {
  color: #1f2330;
  font-family: 'montserrat-bold';
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: #60708a;
  font-family: 'montserrat-bold';
  font-size: 14px;
}

.dialog-input ::v-deep .v-input__slot {
  border-radius: 10px !important;
}

.receipt-upload {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 56px;
  border: 1px dashed #9cc9f2;
  border-radius: 12px;
  background: #f8fbff;
  color: #60708a;
  cursor: pointer;
  font-family: 'montserrat-bold';
}

.selected-documents {
  display: grid;
  gap: 8px;
  margin-top: 12px;
}

.selected-document {
  display: grid;
  grid-template-columns: 24px 1fr auto;
  align-items: center;
  gap: 10px;
  min-height: 42px;
  padding: 0 12px;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background: #f8fbff;
  color: #1f2330;
  font-family: 'montserrat-medium';
}

.selected-document span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selected-document small {
  color: #60708a;
  font-family: 'montserrat-bold';
}

.hidden-input {
  display: none;
}

.payment-note {
  display: flex;
  gap: 10px;
  margin-top: 18px;
  padding: 14px;
  color: #60708a;
  font-family: 'montserrat-medium';
}

.payment-actions {
  padding: 20px 32px 28px;
}

.invoice-actions {
  padding: 28px 32px 32px;
}

.payment-btn {
  min-height: 44px !important;
  padding: 0 24px !important;
  font-family: 'montserrat-bold';
  text-transform: none;
}

@media (max-width: 960px) {
  .revolving-credit-page .container {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  .page-title {
    font-size: 28px;
  }

  .tab-header-row,
  .bank-card__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .tab-actions {
    justify-content: stretch;
    width: 100%;
  }

  .tab-actions .v-btn {
    width: 100%;
  }

  .bank-row {
    grid-template-columns: 1fr;
  }
}
</style>
